/**
 * SOLID Review
 * S — Strong. One concern: the round on the server, as the contracted `ClientRoundServer` port.
 * O — Good. A new round op is one method over the adapter.
 * L — Strong. Implements `IPortClientRoundServer` exactly.
 * I — Strong. Exactly the port's ops, each one call to the adapter.
 * D — Strong. The Application's adapter interface; the Apollo adapter arrives by constructor.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPayloadRoundMove, IPayloadBelieve, IPayloadRoomRead, IPayloadLobbyRound } from '../../domain/ports/schemas/TableMoves';
import type { IPortClientRoundServer, IPayloadServerStartRound, IPayloadServerClaim } from '../../application/ports/contracts';
import type { IAdapterApolloGame } from '../../application/ports/IAdapterApolloGame';
import { fromAdapter, doneFromAdapter } from './wireResult';

/**
 * THE ROUND GATEWAY (frontend strict-ports plan, FE1, 2026-10-03), over the same `ApolloGameAdapter`
 * the React handlers called directly. What changes is the edge: every answer now passes the port's
 * contract on its way to the Application — the kernel guard parses it — where the handlers cast it.
 * A field the server stops sending is a `CONTRACT` failure here, not an `undefined` in the store.
 */
export class ClientRoundServerAdapter implements IPortClientRoundServer {
  constructor(private readonly game: IAdapterApolloGame) {}

  async startRound(_ctx: IContextRequest, command: IPayloadServerStartRound) {
    return fromAdapter(await this.game.startRound(command.roomId, command.rollerId));
  }

  async rollDice(_ctx: IContextRequest, move: IPayloadRoundMove) {
    return doneFromAdapter(await this.game.rollDice(move.roundId, move.playerId));
  }

  async peekDice(_ctx: IContextRequest, move: IPayloadRoundMove) {
    return fromAdapter(await this.game.peekDice(move.roundId, move.playerId));
  }

  async announceClaim(_ctx: IContextRequest, claim: IPayloadServerClaim) {
    return fromAdapter(await this.game.announceClaim(claim.roundId, claim.playerId, claim.score));
  }

  async announceBlind(_ctx: IContextRequest, claim: IPayloadServerClaim) {
    return fromAdapter(await this.game.announceBlind(claim.roundId, claim.playerId, claim.score));
  }

  async believeClaim(_ctx: IContextRequest, believe: IPayloadBelieve) {
    return fromAdapter(await this.game.believeClaim(believe.roundId, believe.playerId, believe.roomId, believe.minimumScore));
  }

  async liftCup(_ctx: IContextRequest, move: IPayloadRoundMove) {
    return doneFromAdapter(await this.game.liftCup(move.roundId, move.playerId));
  }

  async getActiveRound(_ctx: IContextRequest, read: IPayloadRoomRead) {
    return fromAdapter(await this.game.getActiveRound(read.roomId), (round): IPayloadLobbyRound | null =>
      (round === null ? null : { roundId: round.roundId, rollerId: round.rollerId }));
  }

  async getActiveRoundForRestore(_ctx: IContextRequest, read: IPayloadRoomRead) {
    return fromAdapter(await this.game.getActiveRoundForRestore(read.roomId), (round) =>
      (round === null ? null : { ...round, playerLives: [...round.playerLives] }));
  }
}
